import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getPlatformSettings, updatePlatformSettings } from '../axios/ServerRequest';
import LocalStorage from '../utils/LocalStorage';
import {
  DEFAULT_PLATFORM_TIMEZONE,
  getPlatformTimezone,
  setPlatformTimezone,
  formatPlatformDate as formatDateUtil,
  formatPlatformDateTime as formatDateTimeUtil,
  formatPlatformMonthYear as formatMonthYearUtil,
} from '../utils/platformDate';

interface PlatformTimezoneContextValue {
  timezone: string;
  loaded: boolean;
  saving: boolean;
  formatDate: (value?: string | Date | null, options?: Intl.DateTimeFormatOptions) => string;
  formatDateTime: (value?: string | Date | null, options?: Intl.DateTimeFormatOptions) => string;
  formatMonthYear: (value?: string | Date | null) => string;
  saveTimezone: (timezone: string) => Promise<void>;
  reloadTimezone: () => Promise<void>;
}

const PlatformTimezoneContext = createContext<PlatformTimezoneContextValue>({
  timezone: DEFAULT_PLATFORM_TIMEZONE,
  loaded: false,
  saving: false,
  formatDate: formatDateUtil,
  formatDateTime: formatDateTimeUtil,
  formatMonthYear: formatMonthYearUtil,
  saveTimezone: async () => {},
  reloadTimezone: async () => {},
});

export const PlatformTimezoneProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [timezone, setTimezone] = useState(getPlatformTimezone());
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  const applyTimezone = useCallback((tz: string) => {
    setPlatformTimezone(tz);
    setTimezone(tz);
  }, []);

  const reloadTimezone = useCallback(async () => {
    const token = LocalStorage.getApiKey();
    if (!token) {
      applyTimezone(DEFAULT_PLATFORM_TIMEZONE);
      setLoaded(true);
      return;
    }

    try {
      const res = await getPlatformSettings();
      const tz = res?.data?.timezone;
      if (tz) {
        applyTimezone(tz);
      }
    } catch {
      applyTimezone(DEFAULT_PLATFORM_TIMEZONE);
    } finally {
      setLoaded(true);
    }
  }, [applyTimezone]);

  useEffect(() => {
    let cancelled = false;
    reloadTimezone().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [reloadTimezone]);

  const saveTimezone = useCallback(
    async (newTimezone: string) => {
      setSaving(true);
      try {
        const res = await updatePlatformSettings({
          timezone: newTimezone,
          portalType: 'FREELANCER',
        });
        const tz = res?.data?.timezone ?? newTimezone;
        applyTimezone(tz);
      } finally {
        setSaving(false);
      }
    },
    [applyTimezone],
  );

  const formatDate = useCallback(
    (value?: string | Date | null, options?: Intl.DateTimeFormatOptions) =>
      formatDateUtil(value, options),
    [timezone],
  );

  const formatDateTime = useCallback(
    (value?: string | Date | null, options?: Intl.DateTimeFormatOptions) =>
      formatDateTimeUtil(value, options),
    [timezone],
  );

  const formatMonthYear = useCallback(
    (value?: string | Date | null) => formatMonthYearUtil(value),
    [timezone],
  );

  return (
    <PlatformTimezoneContext.Provider
      value={{
        timezone,
        loaded,
        saving,
        formatDate,
        formatDateTime,
        formatMonthYear,
        saveTimezone,
        reloadTimezone,
      }}
    >
      {children}
    </PlatformTimezoneContext.Provider>
  );
};

export function usePlatformDate() {
  return useContext(PlatformTimezoneContext);
}
